import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Send, Pencil, X } from "lucide-react";

// YYYY-MM-DDTHH:mm in local time, for <input type="datetime-local">
const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [publishingId, setPublishingId] = useState(null);
  // { id, title } of the post we're currently asking a publish date for
  const [pickerFor, setPickerFor] = useState(null);
  const [pickerDate, setPickerDate] = useState("");
  const [pickerFeatured, setPickerFeatured] = useState(false);

  useEffect(() => {
    http
      .get("/admin/draft-posts")
      .then(({ data }) => setDrafts(data || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const openPicker = (post) => {
    setPickerFor(post);
    setPickerDate(nowLocal());
    setPickerFeatured(Boolean(post.featured));
  };

  const closePicker = () => {
    setPickerFor(null);
    setPickerDate("");
    setPickerFeatured(false);
  };

  const confirmPublish = async () => {
    if (!pickerFor) return;
    setPublishingId(pickerFor.id);
    try {
      await http.put(`/posts/${pickerFor.id}`, {
        status: "published",
        featured: pickerFeatured,
        created_at: pickerDate
          ? new Date(pickerDate).toISOString()
          : new Date().toISOString(),
      });
      toast.success("Published.");
      closePicker();
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPublishingId(null);
    }
  };

  let body;
  if (loading) {
    body = (
      <div className="p-8 text-center text-[#66726d] font-mono text-xs uppercase tracking-widest animate-pulse">
        loading…
      </div>
    );
  } else if (drafts.length === 0) {
    body = (
      <div className="p-12 text-center text-[#66726d]">
        No drafts. Anything you create with status &quot;draft&quot; will appear here.
      </div>
    );
  } else {
    body = drafts.map((p) => (
      <div
        key={p.id}
        className="grid grid-cols-12 gap-4 items-start px-5 py-5 border-t"
        style={{ borderColor: "var(--border)" }}
        data-testid={`draft-row-${p.slug}`}
      >
        <div className="col-span-12 md:col-span-7">
          <div className="font-serif text-xl text-[#151e1a] mb-1">{p.title}</div>
          <div className="text-sm text-[#404e48] font-light leading-snug mb-2 line-clamp-2">
            {p.excerpt}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
            <span>{p.author_name}</span>
            <span>·</span>
            <span>{p.category}</span>
            <span>·</span>
            <span>{p.read_time} min</span>
            {p.focus_keyword && (
              <>
                <span>·</span>
                <span className="text-[#1e4a35] normal-case tracking-normal font-sans">
                  focus: {p.focus_keyword}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="col-span-12 md:col-span-5 flex flex-wrap items-center md:justify-end gap-2">
          <Link
            to={`/admin/posts/${p.id}/edit`}
            className="inline-flex items-center gap-1 px-3 py-2 border border-[#151e1a] text-[#151e1a] hover:bg-[#151e1a] hover:text-[#f8f4ec] transition-colors text-xs font-mono uppercase tracking-widest"
            data-testid={`edit-draft-${p.slug}`}
          >
            <Pencil size={13} /> Edit
          </Link>
          <button
            onClick={() => openPicker(p)}
            disabled={publishingId === p.id}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid={`publish-draft-${p.slug}`}
          >
            <Send size={13} /> Publish
          </button>
        </div>
      </div>
    ));
  }

  return (
    <div
      className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20"
      data-testid="admin-drafts-page"
    >
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#66726d] hover:text-[#1e4a35] mb-6"
        data-testid="back-to-admin"
      >
        <ChevronLeft size={14} /> Back to dashboard
      </Link>

      <div className="mb-12">
        <p className="overline mb-3">Admin · Drafts</p>
        <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
          Draft Posts
        </h1>
        <p className="mt-4 text-[#404e48] max-w-2xl font-light">
          {loading
            ? "…"
            : `${drafts.length} ${drafts.length === 1 ? "post is" : "posts are"} ready to go. Review, edit if needed, then publish whenever you like. Publishing immediately notifies Bing, Yandex, and DuckDuckGo via IndexNow.`}
        </p>
      </div>

      <div className="border" style={{ borderColor: "var(--border)" }}>
        {body}
      </div>

      {pickerFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: "rgba(26,29,22,0.55)" }}
          onClick={closePicker}
          data-testid="publish-picker-overlay"
        >
          <div
            className="bg-[#f8f4ec] max-w-md w-full p-6 border"
            style={{ borderColor: "var(--border)" }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="overline mb-2">Publish</p>
                <h2 className="font-serif text-2xl text-[#151e1a] leading-tight">
                  {pickerFor.title}
                </h2>
              </div>
              <button
                onClick={closePicker}
                className="p-1 text-[#66726d] hover:text-[#151e1a]"
                aria-label="Cancel"
                data-testid="close-publish-picker"
              >
                <X size={18} />
              </button>
            </div>

            <label className="block mb-1 font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
              Publish date
            </label>
            <input
              type="datetime-local"
              value={pickerDate}
              onChange={(e) => setPickerDate(e.target.value)}
              className="ghost-input"
              data-testid="publish-date-input"
            />
            <p className="mt-1 font-mono text-[10px] text-[#66726d]">
              Defaults to the current moment. Adjust if you want to back-date or future-date.
            </p>

            <label className="flex items-center gap-3 mt-5 cursor-pointer">
              <input
                type="checkbox"
                checked={pickerFeatured}
                onChange={(e) => setPickerFeatured(e.target.checked)}
                data-testid="publish-featured-checkbox"
              />
              <span className="font-mono text-[10px] uppercase tracking-widest">
                Feature on homepage hero
              </span>
            </label>

            <div className="flex gap-3 mt-6">
              <button
                onClick={confirmPublish}
                disabled={publishingId === pickerFor.id}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="confirm-publish-button"
              >
                <Send size={13} />
                {publishingId === pickerFor.id ? "Publishing…" : "Publish"}
              </button>
              <button
                onClick={closePicker}
                className="btn-secondary"
                data-testid="cancel-publish-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
